import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';
import {
  CourseAssignmentStateEnum,
  CourseClassroomStateEnum,
  CourseDefinitionContentTypeEnum,
} from 'src/api/ProactApiClient';
import {
  buildFieldsByInvariantId,
  evaluateVisibilityMap,
} from 'src/app/forms/engine/visibilityEvaluator';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {createInitialFormStateV2} from 'src/types/FormV2Mapping';
import {generateFormSchema} from 'src/types/FormV2Schema';
import {mapEnumToListOption} from 'src/utilities/enumUtils';
import {
  DemonstrationFormMeta,
  LessonFormMeta,
  QuestionFormMeta,
  QuizFormMeta,
} from './courseFormMeta';

export const TrainingDocumentTitle: string = 'Training';

export function renderContentHeaderText(
  content: CourseDefinitionContentResponse
): string {
  let text = 'Invalid';
  switch (content.type) {
    case CourseDefinitionContentTypeEnum.Lesson:
      text = content.lesson?.headerText ?? 'Invalid';
      break;
    case CourseDefinitionContentTypeEnum.Quiz:
      text = content.quiz?.headerText ?? 'Invalid';
      break;

    case CourseDefinitionContentTypeEnum.Demonstration:
      text = content.demonstration?.headerText ?? 'Invalid';
      break;

    default:
      text = 'Invalid';
      break;
  }

  return text.abbreviate(15);
}

// Validates form data against metadata using the same visibility-aware
// zod schema the mounted ProactForm resolver would use, without requiring
// the form to be mounted (content editors for inactive language tabs are
// not mounted, so live per-field validity is not always available).
function validateAgainstMeta(
  formMeta: IFormFieldV2[],
  data: IFormDataV2
): string | undefined {
  // Mirror what the mounted ProactForm would hold: metadata defaults
  // (builderOptions.defaultValue) filled in, real values on top.
  const seeded: IFormDataV2 = {...createInitialFormStateV2(formMeta)};
  Object.keys(data ?? {}).forEach(key => {
    if (data[key] !== undefined) {
      seeded[key] = data[key];
    }
  });

  const fieldsByInvariantId = buildFieldsByInvariantId(formMeta);
  const visibility = evaluateVisibilityMap(
    formMeta,
    seeded,
    fieldsByInvariantId
  );
  const visibleFields = formMeta.filter(field => visibility[field.name]);
  const result = generateFormSchema(visibleFields).safeParse(seeded);
  return result.success ? undefined : result.error.issues[0]?.message;
}

export function validateCourseDefinitionContent(
  content: CourseDefinitionContentResponse
): string | undefined {
  const label = renderContentHeaderText(content);

  switch (content.type) {
    case CourseDefinitionContentTypeEnum.Lesson: {
      const error = validateAgainstMeta(LessonFormMeta, {
        ...content.lesson,
        timeLimit: content.timeLimit,
      } as IFormDataV2);
      return error && `"${label}": ${error}`;
    }
    case CourseDefinitionContentTypeEnum.Quiz: {
      const error = validateAgainstMeta(QuizFormMeta, {
        ...content.quiz,
        timeLimit: content.timeLimit,
      } as IFormDataV2);
      if (error) return `"${label}": ${error}`;

      for (const question of content.quiz?.questions ?? []) {
        const questionError = validateAgainstMeta(
          QuestionFormMeta,
          question as IFormDataV2
        );
        if (questionError) {
          return `"${label}", question "${
            question.labelText || 'Untitled'
          }": ${questionError}`;
        }
        if ((question.answers ?? []).some(answer => !answer.text)) {
          return `"${label}", question "${
            question.labelText || 'Untitled'
          }": every answer requires text.`;
        }
      }
      return undefined;
    }
    case CourseDefinitionContentTypeEnum.Demonstration: {
      const error = validateAgainstMeta(
        DemonstrationFormMeta(
          undefined,
          undefined,
          undefined,
          content.demonstration?.skillId != null
        ),
        content.demonstration as IFormDataV2
      );
      return error && `"${label}": ${error}`;
    }
    default:
      return undefined;
  }
}

export function validateCourseDefinitionContents(
  contents: CourseDefinitionContentResponse[]
): string | undefined {
  for (const content of contents) {
    const error = validateCourseDefinitionContent(content);
    if (error) return error;
  }
  return undefined;
}

export interface ContentValidationOffender {
  invariantId: string;
  label: string;
  error: string;
}

/**
 * Per-content validation results (as opposed to `validateCourseDefinitionContents`'s
 * first-error-wins string) for the Build tab's validation panel, which names
 * every offending item and lets the user click through to it.
 */
export function validateCourseDefinitionContentsDetailed(
  contents: CourseDefinitionContentResponse[]
): ContentValidationOffender[] {
  const offenders: ContentValidationOffender[] = [];
  for (const content of contents) {
    const error = validateCourseDefinitionContent(content);
    if (error) {
      offenders.push({
        invariantId: content.invariantId!,
        label: renderContentHeaderText(content),
        error,
      });
    }
  }
  return offenders;
}

export const courseAssignmentStateOptions = mapEnumToListOption(
  CourseAssignmentStateEnum
);

export const courseClassroomStateOptions = mapEnumToListOption(
  CourseClassroomStateEnum
);
